import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Chip – reimagined, zero-Frontile chip / badge / tag component
// ---------------------------------------------------------------------------

/**
 * Visual intent/severity options
 */
export type ChipIntent = 'primary' | 'danger' | 'warning' | 'success' | 'default';

/**
 * Size options
 */
export type ChipSize = 'sm' | 'md' | 'lg';

export interface ChipSignature {
  Args: {
    /** Visual intent */
    intent?: ChipIntent;
    /** Size */
    size?: ChipSize;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLSpanElement;
}

// ---------------------------------------------------------------------------
// Style maps (Tailwind classes only)
// ---------------------------------------------------------------------------

const INTENT_CLASSES: Record<ChipIntent, string> = {
  primary: 'bg-[#006c9a]/10 text-[#006c9a] dark:bg-[#006c9a]/20 dark:text-[#4db8e0]',
  danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const SIZE_CLASSES: Record<ChipSize, string> = {
  sm: 'text-xs px-2 py-0.5 rounded-md',
  md: 'text-sm px-2.5 py-1 rounded-lg',
  lg: 'text-sm px-3 py-1.5 rounded-lg',
};

/**
 * A lightweight status badge / tag used in cards, tables, and list items.
 *
 * ```gts
 * <Chip @intent="success" @size="sm">Active</Chip>
 * ```
 *
 * @public
 */
export default class Chip extends Component<ChipSignature> {
  get intent(): ChipIntent {
    return this.args.intent ?? 'default';
  }

  get size(): ChipSize {
    return this.args.size ?? 'md';
  }

  get intentClass(): string {
    return INTENT_CLASSES[this.intent];
  }

  get sizeClass(): string {
    return SIZE_CLASSES[this.size];
  }

  <template>
    <span
      data-test-chip
      data-test-chip-intent={{this.intent}}
      data-test-chip-size={{this.size}}
      class="inline-flex items-center gap-1 font-medium {{this.intentClass}} {{this.sizeClass}}"
      ...attributes
    >
      {{yield}}
    </span>
  </template>
}
